// ts-rest contract for the upload history of the Uploads page of the Admin
// shell.
//
// The history lists each upload of a file type that the page can upload, with
// its files (`ADMIN_UPLOAD_DOORS`, `upload-history.ts` on the server). The
// page also marks its own upload requests (`upload-surface.ts`): the mark
// gives the name of the upload, and it adds the line of a door that keeps no
// bytes (the two KYC doors, ADR-0009), which has no file to download.
//
// Endpoints:
//   GET /api/admin/upload-history                     — the uploads, newest first
//   GET /api/admin/upload-history/files/:id/content   — the bytes of one file
//
// The download route is a plain binary route of the controller, and not a
// ts-rest entry, because it answers with file bytes. The controller gates both
// on the admin persona (ADR-0004), and it audits each download.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * The doors of the Admin Uploads page, each with its name on the page. The
 * key is the `door` value that the server stores on each kept file. The user
 * gave the rule on 2026-11-05: a file type that the page can upload counts as
 * an admin upload, whatever page took it in. The COAC CA PDF is on the page
 * too, but it is a document and not data, so it stays out.
 */
export const ADMIN_UPLOAD_DOORS: Readonly<Record<string, string>> = {
    valuation_grid: 'Client Valuations',
    dynamics_contacts: 'Dynamics CRM Data',
    contacts: 'Client Book · Contacts',
    client_book_mapping: 'Client Book · Mapping',
    kyc_assessments: 'Client Book · KYC Assessments',
    crm_fact_finds: 'Client Book · CRM Fact Finds',
    benchmark_pci: 'ARC PCI Benchmarks',
    generic_data: 'Performance Data: Generic Data',
    performance: 'Performance Data: Performance',
    tercero_fx_rates: 'Tercero FX Rates',
    rbc_portfolio_holdings: 'Reconciliation: RBC · Portfolio Holdings',
    rbc_valuation: 'Reconciliation: RBC · Valuation',
    rbc_mapping: 'Reconciliation: RBC · Account Mapping',
    lloyds_portal: 'Reconciliation: Lloyds · Portal export',
    lloyds_portfolio_holdings: 'Reconciliation: Lloyds · Portfolio Holdings',
    lloyds_mapping: 'Reconciliation: Lloyds · Account Mapping',
    butterfield_holdings: 'Reconciliation: Butterfield · Holdings',
    butterfield_cash: 'Reconciliation: Butterfield · Cash',
    caceis_holdings: 'Reconciliation: Caceis · Holdings',
    caceis_cash: 'Reconciliation: Caceis · Cash',
    tercero_f1: 'Reconciliation: Tercero · F1 Valuation',
    tercero_f3: 'Reconciliation: Tercero · F3 Valuation',
    tercero_f7: 'Reconciliation: Tercero · F7 Valuation',
    tercero_f1_f7_cash: 'Reconciliation: Tercero · F1/F7 Cash',
    cash_accounts: 'Cash Management',
    overdrawn_cash_accounts: 'Overdrawn Cash',
    order_blotter: 'Trade Blotter',
    coac_workbook: 'Corporate Actions: COAC Log',
    coac_ca_feed: 'Corporate Actions: COAC Book',
    // The KPI Reporting row of the page links to the KPI page, which takes
    // the six monthly Tercero exports. The user counted them in (2026-11-05).
    kpi_performance_dc: 'KPI Reporting: Performance DC',
    kpi_performance_dgw: 'KPI Reporting: Performance DGW',
    kpi_flows_dc: 'KPI Reporting: FlowsGrid DC',
    kpi_flows_dgw: 'KPI Reporting: FlowsGrid DGW',
    kpi_browse_dc: 'KPI Reporting: Browse DC',
    kpi_browse_dgw: 'KPI Reporting: Browse DGW',
};

export const uploadHistoryFileSchema = z.object({
    /** The source upload id of the file. */
    id: z.string(),
    filename: z.string(),
    byteSize: z.number().int().nonnegative().nullable(),
    /** False when the server keeps no bytes for the file. */
    available: z.boolean(),
});
export type UploadHistoryFile = z.infer<typeof uploadHistoryFileSchema>;

export const uploadHistoryRowSchema = z.object({
    /** The id of the first file of the upload. */
    id: z.string(),
    /** The name of the upload on the page, or null for an upload with no name. */
    label: z.string().nullable(),
    /** The door key that took the file in, for example `rbc_valuation`. A value. */
    door: z.string().nullable(),
    uploadedAt: z.string(),
    uploadedByName: z.string().nullable(),
    files: z.array(uploadHistoryFileSchema),
    /** The ingest plan of the upload, for a door that prepares a plan. */
    plan: z
        .object({
            id: z.string(),
            status: z.enum(['draft', 'applied', 'discarded', 'expired']),
        })
        .nullable(),
});
export type UploadHistoryRow = z.infer<typeof uploadHistoryRowSchema>;

export const uploadHistoryResponseSchema = z.object({
    rows: z.array(uploadHistoryRowSchema),
    /** The number of uploads that match, over all pages. */
    total: z.number().int().nonnegative(),
});
export type UploadHistoryResponse = z.infer<typeof uploadHistoryResponseSchema>;

export const adminUploadHistoryContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/admin/upload-history',
            query: z.object({
                /** Matches the name of the upload or the name of a file. */
                search: z.string().optional(),
                page: z.coerce.number().int().min(1).optional(),
                pageSize: z.coerce.number().int().min(1).max(100).optional(),
            }),
            responses: { 200: uploadHistoryResponseSchema },
            summary: 'Admin: the uploads of the Admin Uploads page, newest first.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
